<template>
  <el-dialog
    ref="dialogRef"
    :title="`会员游戏记录详情 - ${memberAccount}`"
    :visible.sync="dialogVisible"
    width="1200px"
    :close-on-click-modal="false"
    class="page-dialog center-dialog"
    @close="handleClose"
    @closed="handleClosed"
  >
    <div v-loading="tableLoading" class="detail-content">
      <!-- 时间范围筛选 -->
      <div class="filter-section">
        <el-form :inline="true" :model="filterForm" size="mini">
          <el-row :gutter="16">
            <el-col :span="12">
              <el-form-item label="时间范围">
                <DateTimeRangePicker v-model="filterForm.timeRange" clearable style="width: 100%" />
              </el-form-item>
            </el-col>
            <el-col :span="12" style="text-align: right">
              <el-button size="mini" type="primary" @click="handleFilter">筛选</el-button>
              <el-button size="mini" @click="handleReset">重置</el-button>
            </el-col>
          </el-row>
        </el-form>
      </div>

      <!-- 游戏记录表格 -->
      <div class="table-section">
        <el-table :data="tableData" border stripe size="small" style="width: 100%" show-summary :summary-method="getSummaries">
          <el-table-column :resizable="false" prop="game_title" label="游戏名称" min-width="150"></el-table-column>
          <el-table-column :resizable="false" prop="game_id" label="游戏编号" min-width="120"></el-table-column>
          <el-table-column :resizable="false" prop="win_amount" label="输赢情况" width="100">
            <template slot-scope="scope">
              <span :class="(scope.row.win_amount || 0) >= 0 ? 'text-positive' : 'text-negative'">
                {{ (scope.row.win_amount || 0) >= 0 ? '+' : '' }}{{ formatAmount(scope.row.win_amount) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column :resizable="false" prop="bet_amount" label="下注金额" width="100">
            <template slot-scope="scope">{{ formatAmount(scope.row.bet_amount) }}</template>
          </el-table-column>
          <el-table-column :resizable="false" prop="created_at" label="下注时间" width="140">
            <template slot-scope="scope">{{ formatTimestamp(scope.row.created_at) }}</template>
          </el-table-column>
          <el-table-column :resizable="false" prop="status" label="状态" width="80" align="center">
            <template slot-scope="scope">
              <el-tag :type="formatStatus(scope.row) === 'Win' ? 'success' : 'danger'">
                {{ formatStatus(scope.row) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column :resizable="false" prop="bet_id" label="注单号" min-width="150" show-overflow-tooltip>
            <template slot-scope="scope">{{ scope.row.bet_id != null ? String(scope.row.bet_id) : '--' }}</template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <template #footer>
      <el-pagination
        :current-page="currentPage"
        :page-sizes="[10, 20, 30, 50]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      ></el-pagination>
    </template>
  </el-dialog>
</template>

<script>
import { getBetList } from '@/api/finance/moneyManagement'
import DateTimeRangePicker from '@/components/DateTimeRangePicker'
import { centsToYuan } from '@/utils/amount'
import { formatTimestamp, toUnixSeconds } from '@/utils/time'

export default {
  name: 'BettingRecordDetailDialog',
  components: { DateTimeRangePicker },
  props: {
    memberAccount: {
      type: String,
      default: '',
    },
    memberUid: {
      type: Number,
      default: 0,
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      filterForm: {
        timeRange: [],
      },
      tableData: [],
      tableLoading: false,
      currentPage: 1,
      pageSize: 10,
      total: 0,
      stats: {
        bet_amount_total: 0,
        win_amount_total: 0,
      },
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      },
    },
  },
  watch: {
    visible(val) {
      if (val && this.memberAccount) {
        this.currentPage = 1
        this.loadRecords()
      }
    },
  },
  methods: {
    formatTimestamp,
    formatAmount(v) {
      if (v == null) return '0.00'
      const yuan = centsToYuan(v)
      return Number(yuan).toFixed(2)
    },
    formatStatus(row) {
      const win = row.win_amount || 0
      return win > 0 ? 'Win' : 'Lose'
    },
    async loadRecords() {
      if (!this.memberAccount && !this.memberUid) return
      this.tableLoading = true
      try {
        const params = {
          page: this.currentPage,
          page_size: this.pageSize,
          username: this.memberAccount || '',
          bet_id: '',
          game_id: '',
          game_title: '',
          inviter_username: '',
          inviter_uid: 0,
          start_time: 0,
          end_time: 0,
        }
        if (this.filterForm.timeRange && this.filterForm.timeRange.length === 2) {
          params.start_time = toUnixSeconds(this.filterForm.timeRange[0]) || 0
          params.end_time = toUnixSeconds(this.filterForm.timeRange[1]) || 0
        }
        const res = await getBetList(params)
        if (res.code === 0 && res.data) {
          this.tableData = res.data.list || []
          this.total = res.data.total || 0
          this.stats = res.data.stats || { bet_amount_total: 0, win_amount_total: 0 }
        } else {
          this.tableData = []
          this.total = 0
          this.stats = { bet_amount_total: 0, win_amount_total: 0 }
        }
      } catch (e) {
        this.$message.error('加载游戏记录失败')
        this.tableData = []
      } finally {
        this.tableLoading = false
      }
    },
    handleFilter() {
      this.currentPage = 1
      this.loadRecords()
    },
    handleReset() {
      this.filterForm.timeRange = []
      this.currentPage = 1
      this.loadRecords()
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.loadRecords()
    },
    handleCurrentChange(page) {
      this.currentPage = page
      this.loadRecords()
    },
    handleClose() {
      this.$emit('update:visible', false)
      this.$emit('close')
    },
    handleClosed() {
      this.filterForm.timeRange = []
      this.tableData = []
      this.currentPage = 1
      this.pageSize = 10
      this.total = 0
      this.stats = { bet_amount_total: 0, win_amount_total: 0 }
    },
    getSummaries(param) {
      const { columns } = param
      const sums = []
      columns.forEach((col, idx) => {
        if (idx === 0) {
          sums[idx] = '投注分数总计'
          return
        }
        if (col.property === 'win_amount') {
          sums[idx] = (this.stats.win_amount_total >= 0 ? '+' : '') + this.formatAmount(this.stats.win_amount_total)
        } else if (col.property === 'bet_amount') {
          sums[idx] = this.formatAmount(this.stats.bet_amount_total)
        } else {
          sums[idx] = ''
        }
      })
      return sums
    },
  },
}
</script>

<style scoped>
.filter-section {
  margin-bottom: 16px;
}
.filter-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.table-section {
  margin-bottom: 0;
}
.text-positive {
  color: #67c23a;
  font-weight: 500;
}
.text-negative {
  color: #f56c6c;
  font-weight: 500;
}
</style>
